import { useEffect, useRef, useState, useCallback } from "react";
import { CaretDown, Check, ArrowCounterClockwise } from "@phosphor-icons/react";

/**
 * iOS-style wheel picker for logging reps × kg.
 *
 * UX:
 *  - Full-width bottom-sheet modal overlayed on the workout view.
 *  - Two scrollable columns: REPS (left) and KG (right).
 *  - Native scroll-snap gives momentum + snap on touch/wheel without any
 *    custom gesture handling (works on iOS Safari, Android Chrome, desktop).
 *  - A fixed highlight rounded-pill sits at the centre; whichever rows
 *    align inside it are the current selection.
 *  - Red circular check button confirms; caret closes without saving;
 *    clock-icon resets both wheels to the "previous set" values.
 *
 *  Accessibility: each wheel is a real scroll container with snap points;
 *  tapping a row scrolls it into the centre. Keyboard users can Tab to
 *  a wheel and use ↑/↓ to step through values.
 */

const ITEM_HEIGHT = 44;         // must match CSS
const VISIBLE_ITEMS = 7;        // odd number so we have a precise centre row
const WHEEL_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS;

// Generate values up-front (cheap — small arrays).
const KG_INT_VALUES = Array.from({ length: 301 }, (_, i) => i); // 0..300
const KG_DEC_VALUES = [0, 5];                                    // .0 / .5
const REPS_VALUES = Array.from({ length: 101 }, (_, i) => i);   // 0..100

function formatKgInt(v) { return String(v); }
function formatKgDec(v) { return v === 5 ? "5" : "0"; }

// Stable helpers used by both module-level fns and the component hook below.
function findIdxIn(arr, v, fallback = 0) {
  if (v === null || v === undefined || v === "") return fallback;
  const idx = arr.indexOf(typeof v === "number" ? v : parseFloat(v));
  return idx >= 0 ? idx : fallback;
}

// Decompose a "kg" number into (intIdx, decIdx) where dec is either 0 or 5.
// Pure / module-level so it's stable across renders and safe in deps arrays.
function splitKg(v) {
  if (v === "" || v === null || v === undefined) return [null, null];
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return [null, null];
  const intPart = Math.floor(n);
  const frac = +(n - intPart).toFixed(2);
  const decIdx = frac >= 0.25 && frac < 0.75 ? 1 : 0; // snap to .0 / .5
  const intIdx = findIdxIn(KG_INT_VALUES, intPart, 0);
  return [intIdx, decIdx];
}

function WheelColumn({
  label, values, selectedIndex, onIndexChange, formatter, unit, testid, active, onActivate,
  showRing = true, align = "center",
}) {
  const ref = useRef(null);
  const rafRef = useRef(null);
  const suppressScrollEvent = useRef(false);
  const hasInitScrolled = useRef(false);
  // Track the last target we scrolled to programmatically so the callback ref
  // knows what to restore if the browser re-snaps during the sheet's slide-up
  // animation (Chromium sometimes shifts scrollTop by a few items when
  // `scroll-snap-type: mandatory` re-evaluates once the container settles).
  const targetIndexRef = useRef(selectedIndex);
  targetIndexRef.current = selectedIndex;

  const tick = () => {
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(6);
      }
    } catch (err) { console.debug("[LogPickerSheet]", err); }
  };

  // Programmatically scroll to the current selected index
  const scrollToIndex = useCallback((idx, behavior = "smooth") => {
    const el = ref.current;
    if (!el) return;
    suppressScrollEvent.current = true;
    el.scrollTo({ top: idx * ITEM_HEIGHT, behavior });
    setTimeout(() => { suppressScrollEvent.current = false; }, 350);
  }, []);

  // Callback ref: runs synchronously when the scroll container is attached to
  // the DOM (BEFORE useLayoutEffect, BEFORE paint). This is the earliest
  // possible moment to set scrollTop and it guarantees the first frame the
  // user sees already has the target row centred.
  //
  // We also schedule two rAF passes + a 260ms safety tick because the sheet
  // slides up with a CSS transform animation and Chromium occasionally
  // re-snaps to a nearby item once the container's on-screen position
  // stabilises, silently pulling the wheel back. Re-asserting scrollTop after
  // each rAF and once more at the tail of the animation locks the wheel to
  // the target index no matter what the browser does in between.
  const attachRef = useCallback((el) => {
    ref.current = el;
    if (!el) return;
    const idx = targetIndexRef.current;
    el.scrollTop = idx * ITEM_HEIGHT;
    // Kick off snap re-alignment guards.
    const raf1 = requestAnimationFrame(() => {
      const cur = ref.current;
      if (!cur) return;
      if (Math.round(cur.scrollTop / ITEM_HEIGHT) !== targetIndexRef.current) {
        cur.scrollTop = targetIndexRef.current * ITEM_HEIGHT;
      }
      requestAnimationFrame(() => {
        const c2 = ref.current;
        if (!c2) return;
        if (Math.round(c2.scrollTop / ITEM_HEIGHT) !== targetIndexRef.current) {
          c2.scrollTop = targetIndexRef.current * ITEM_HEIGHT;
        }
      });
    });
    setTimeout(() => {
      const c3 = ref.current;
      if (!c3) return;
      if (Math.round(c3.scrollTop / ITEM_HEIGHT) !== targetIndexRef.current) {
        c3.scrollTop = targetIndexRef.current * ITEM_HEIGHT;
      }
    }, 260);
    setTimeout(() => { hasInitScrolled.current = true; }, 500);
    // rAF cleanup on unmount happens via callback ref receiving `null`.
    // eslint-disable-next-line no-unused-expressions
    raf1;
  }, []);

  // When `selectedIndex` changes AFTER mount (restore-previous, keyboard),
  // sync the DOM scroll. Use "auto" to avoid the long smooth-scroll →
  // intermediate-event race that caused wheels to overshoot on large deltas.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const currentIdx = Math.round(el.scrollTop / ITEM_HEIGHT);
    if (currentIdx !== selectedIndex) scrollToIndex(selectedIndex, "auto");
  }, [selectedIndex, scrollToIndex]);

  const handleScroll = useCallback(() => {
    if (suppressScrollEvent.current) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const idx = Math.max(0, Math.min(values.length - 1,
        Math.round(el.scrollTop / ITEM_HEIGHT)));
      if (idx !== selectedIndex) {
        onIndexChange(idx);
        // Haptic tick per item change (only once the initial scroll-to has settled)
        if (hasInitScrolled.current) tick();
      }
    });
  }, [values.length, selectedIndex, onIndexChange]);

  // Click a row to jump to it
  const handleItemClick = (idx) => {
    onIndexChange(idx);
    scrollToIndex(idx, "smooth");
    if (!active) onActivate?.();
  };

  // Keyboard support (↑/↓)
  const handleKey = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.max(0, selectedIndex - 1);
      onIndexChange(next);
      scrollToIndex(next, "smooth");
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(values.length - 1, selectedIndex + 1);
      onIndexChange(next);
      scrollToIndex(next, "smooth");
    }
  };

  // Pad top/bottom so first and last values can reach the centre
  const PAD = (VISIBLE_ITEMS - 1) / 2;

  return (
    <div
      className="flex-1 relative min-w-0"
      data-testid={`${testid}-col`}
      onFocus={onActivate}
    >
      <div
        ref={attachRef}
        onScroll={handleScroll}
        onKeyDown={handleKey}
        onMouseDown={onActivate}
        onTouchStart={onActivate}
        tabIndex={0}
        role="listbox"
        aria-label={label}
        className={`overflow-y-scroll no-scrollbar outline-none transition-[box-shadow] rounded-2xl ${
          active && showRing ? "ring-2 ring-[#007AFF]/60" : ""
        }`}
        style={{
          height: WHEEL_HEIGHT,
          scrollSnapType: "y mandatory",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
        data-testid={`${testid}-scroller`}
      >
        {/* top padding */}
        {Array.from({ length: PAD }, (_, i) => (
          <div key={`pad-t-${i}`} style={{ height: ITEM_HEIGHT }} aria-hidden />
        ))}
        {values.map((v, idx) => {
          const isCentre = idx === selectedIndex;
          const dist = Math.abs(idx - selectedIndex);
          // Fade + compress items further from centre
          const opacity = Math.max(0.25, 1 - dist * 0.22);
          const scale = Math.max(0.82, 1 - dist * 0.06);
          return (
            <button
              type="button"
              key={`${testid}-${v}`}
              onClick={() => handleItemClick(idx)}
              data-testid={`${testid}-opt-${v}`}
              className="w-full flex items-center font-mono font-semibold select-none"
              style={{
                height: ITEM_HEIGHT,
                scrollSnapAlign: "center",
                scrollSnapStop: "always",
                color: isCentre ? "#FFFFFF" : "#B0B0B0",
                opacity,
                transform: `scale(${scale})`,
                fontSize: isCentre ? 28 : 24,
                lineHeight: `${ITEM_HEIGHT}px`,
                transition: "color 120ms, opacity 120ms, transform 120ms, font-size 120ms",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                justifyContent: align === "right" ? "flex-end" : align === "left" ? "flex-start" : "center",
                paddingLeft: align === "left" ? 4 : 0,
                paddingRight: align === "right" ? 4 : 0,
              }}
            >
              {formatter(v)}
              {isCentre && unit && (
                <span
                  className="ml-2 text-[11px] font-bold uppercase tracking-wider text-zinc-400"
                  style={{ letterSpacing: "0.08em" }}
                >
                  {unit}
                </span>
              )}
            </button>
          );
        })}
        {/* bottom padding */}
        {Array.from({ length: PAD }, (_, i) => (
          <div key={`pad-b-${i}`} style={{ height: ITEM_HEIGHT }} aria-hidden />
        ))}
      </div>
    </div>
  );
}

// Nearest-match index lookup (module-level so it can be reused in lazy state init).
function findNearestIdx(arr, v, fallback = 0) {
  if (v === "" || v === null || v === undefined) return fallback;
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return fallback;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < arr.length; i += 1) {
    const d = Math.abs(arr[i] - n);
    if (d < bestDist) { bestDist = d; best = i; }
  }
  return best;
}

// Compute the three wheel indices from the (initial, previous) prop pair.
// Extracted so both lazy state init AND the "restore previous" effect can share it.
function computeInitialIndices(initialKg, initialReps, previousKg, previousReps) {
  const fromInitial = splitKg(initialKg);
  const fromPrev = splitKg(previousKg);
  const kgIntIdx = fromInitial[0] ?? fromPrev[0] ?? 0;
  const kgDecIdx = fromInitial[1] ?? fromPrev[1] ?? 0;
  const repsIdx = findNearestIdx(
    REPS_VALUES,
    initialReps,
    findNearestIdx(REPS_VALUES, previousReps, 0),
  );
  return { kgIntIdx, kgDecIdx, repsIdx };
}

export function LogPickerSheet({
  open,
  setIndex,          // 0-based index of the set being edited
  totalSets,
  initialKg,
  initialReps,
  previousKg,
  previousReps,
  onConfirm,         // (kg, reps) => void
  onClose,
  onRestore,         // fill with previous-session values
}) {
  // Lazy state init: the wheels start at the correct position BEFORE the first
  // render, so WheelColumn's mount effect can scroll instantly (behavior "auto")
  // without any race with the subsequent smooth-scroll from a useEffect update.
  // This is what makes the "prima serie" of every exercise auto-suggest the
  // reps/kg from the last recorded session — the previous logic *computed* the
  // correct target indices but only *after* mount, giving the smooth-scroll a
  // large delta that outlasted the scroll-event suppression window (350ms) and
  // was overwritten by intermediate scroll positions.
  const [kgIntIdx, setKgIntIdx] = useState(
    () => computeInitialIndices(initialKg, initialReps, previousKg, previousReps).kgIntIdx
  );
  const [kgDecIdx, setKgDecIdx] = useState(
    () => computeInitialIndices(initialKg, initialReps, previousKg, previousReps).kgDecIdx
  );
  const [repsIdx, setRepsIdx] = useState(
    () => computeInitialIndices(initialKg, initialReps, previousKg, previousReps).repsIdx
  );
  const [active, setActive] = useState("reps"); // "reps" | "kg_int" | "kg_dec"

  // When props change WHILE the sheet is open (e.g. the user tapped the
  // "restore previous" button, which updates `sets` in the parent and thus
  // re-emits new initialKg/initialReps), sync the wheels to the new target.
  // On the very first mount this is a no-op because the state already equals
  // the computed indices (React skips the update).
  useEffect(() => {
    if (!open) return;
    const next = computeInitialIndices(initialKg, initialReps, previousKg, previousReps);
    setKgIntIdx(next.kgIntIdx);
    setKgDecIdx(next.kgDecIdx);
    setRepsIdx(next.repsIdx);
    setActive("reps");
  }, [open, initialKg, initialReps, previousKg, previousReps]);

  const selectedKg = KG_INT_VALUES[kgIntIdx] + (KG_DEC_VALUES[kgDecIdx] === 5 ? 0.5 : 0);
  const selectedReps = REPS_VALUES[repsIdx];

  // Close on Escape / submit on Enter / Tab cycles between the 3 wheels
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter") {
        e.preventDefault();
        try {
          if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
            navigator.vibrate([12, 30, 12]);
          }
        } catch (err) { console.debug("[LogPickerSheet]", err); }
        onConfirm?.(selectedKg, selectedReps);
      }
      if (e.key === "Tab") {
        e.preventDefault();
        setActive((a) => (
          a === "reps" ? "kg_int" : a === "kg_int" ? "kg_dec" : "reps"
        ));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, selectedKg, selectedReps, onClose, onConfirm]);

  if (!open) return null;

  const submitConfirm = () => {
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate([12, 30, 12]); // short-strong-short pattern: feels like a "click"
      }
    } catch (err) { console.debug("[LogPickerSheet]", err); }
    onConfirm?.(selectedKg, selectedReps);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center"
      data-testid="log-picker-sheet"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Chiudi"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        data-testid="log-picker-backdrop"
      />

      {/* Sheet */}
      <div
        className="relative w-full max-w-md bg-[#1A1A1A] border-t border-[#262626] rounded-t-3xl pt-2 pb-5 px-4 animate-[slideUp_220ms_ease-out]"
        style={{ boxShadow: "0 -24px 48px -12px rgba(0,0,0,0.6)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grabber */}
        <div className="mx-auto w-10 h-1 rounded-full bg-[#3A3A3A] mb-3" aria-hidden />

        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center text-white active:scale-95 transition-transform"
            aria-label="Chiudi"
            data-testid="log-picker-close"
          >
            <CaretDown size={18} weight="bold" />
          </button>
          <p className="text-base font-semibold text-white tracking-tight">
            Serie {setIndex + 1}
            {totalSets ? <span className="text-zinc-500 font-normal"> di {totalSets}</span> : null}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRestore}
              disabled={!previousKg && !previousReps}
              className="w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center text-zinc-300 disabled:opacity-30 active:scale-95 transition-transform"
              aria-label="Riprendi valori precedenti"
              data-testid="log-picker-restore"
              title="Riprendi valori dell'ultima sessione"
            >
              <ArrowCounterClockwise size={16} weight="bold" />
            </button>
            <button
              type="button"
              onClick={submitConfirm}
              className="w-10 h-10 rounded-full bg-[#FF3B30] flex items-center justify-center text-white active:scale-95 transition-transform"
              aria-label="Conferma"
              data-testid="log-picker-confirm"
            >
              <Check size={20} weight="bold" />
            </button>
          </div>
        </div>

        {/* Wheels */}
        <div className="relative mt-1" style={{ height: WHEEL_HEIGHT }}>
          {/* Centre selection highlight */}
          <div
            aria-hidden
            className="absolute left-2 right-2 pointer-events-none rounded-2xl bg-[#2A2A2A]"
            style={{
              top: (WHEEL_HEIGHT - ITEM_HEIGHT) / 2,
              height: ITEM_HEIGHT,
            }}
          />
          {/* Top / bottom gradients to fade far items */}
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 pointer-events-none"
            style={{
              height: WHEEL_HEIGHT / 2 - ITEM_HEIGHT / 2,
              background: "linear-gradient(to bottom, #1A1A1A 0%, rgba(26,26,26,0) 100%)",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            style={{
              height: WHEEL_HEIGHT / 2 - ITEM_HEIGHT / 2,
              background: "linear-gradient(to top, #1A1A1A 0%, rgba(26,26,26,0) 100%)",
            }}
          />
          <div className="relative flex items-stretch">
            {/* REPS column (left) */}
            <WheelColumn
              label="Ripetizioni"
              testid="wheel-reps"
              values={REPS_VALUES}
              selectedIndex={repsIdx}
              onIndexChange={setRepsIdx}
              formatter={(v) => String(v)}
              active={active === "reps"}
              onActivate={() => setActive("reps")}
            />
            {/* Separator × */}
            <div
              className="flex items-center justify-center select-none pointer-events-none"
              style={{ width: 34 }}
              aria-hidden
            >
              <span className="text-zinc-500 font-light" style={{ fontSize: 22 }}>×</span>
            </div>
            {/* KG frame — houses the integer + decimal wheels in one rounded container */}
            <div
              className={`flex-1 flex relative rounded-2xl transition-[box-shadow] ${
                active === "kg_int" || active === "kg_dec" ? "ring-2 ring-[#007AFF]/60" : ""
              }`}
              data-testid="wheel-kg-frame"
            >
              <WheelColumn
                label="Kg (intero)"
                testid="wheel-kg-int"
                values={KG_INT_VALUES}
                selectedIndex={kgIntIdx}
                onIndexChange={setKgIntIdx}
                formatter={formatKgInt}
                active={active === "kg_int"}
                showRing={false}
                onActivate={() => setActive("kg_int")}
                align="right"
              />
              {/* Decimal dot separator */}
              <div
                className="flex items-center justify-center select-none pointer-events-none"
                style={{ width: 14 }}
                aria-hidden
              >
                <span
                  className="text-white font-bold"
                  style={{ fontSize: 24, transform: "translateY(2px)" }}
                >
                  .
                </span>
              </div>
              <WheelColumn
                label="Kg (decimali)"
                testid="wheel-kg-dec"
                values={KG_DEC_VALUES}
                selectedIndex={kgDecIdx}
                onIndexChange={setKgDecIdx}
                formatter={formatKgDec}
                active={active === "kg_dec"}
                showRing={false}
                unit="kg"
                onActivate={() => setActive("kg_dec")}
                align="left"
              />
            </div>
          </div>
        </div>

        {/* Tab hint */}
        <div className="flex justify-center mt-3 gap-2" aria-hidden>
          <span
            className={`h-1.5 rounded-full transition-all ${
              active === "reps" ? "w-6 bg-[#007AFF]" : "w-1.5 bg-[#3A3A3A]"
            }`}
          />
          <span
            className={`h-1.5 rounded-full transition-all ${
              active === "kg_int" || active === "kg_dec" ? "w-6 bg-[#007AFF]" : "w-1.5 bg-[#3A3A3A]"
            }`}
          />
        </div>

        {/* Save CTA (alternative to red check for desktop users) */}
        <button
          type="button"
          onClick={submitConfirm}
          className="mt-4 w-full py-3 rounded-xl bg-[#007AFF] hover:bg-[#3395FF] text-white font-semibold text-sm active:scale-[0.99] transition-transform"
          data-testid="log-picker-save"
        >
          Salva {selectedReps} × {selectedKg.toFixed(1)} kg
        </button>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
